import React, { useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext, useWatch } from 'react-hook-form'
// @ts-ignore
import { CheckSolid, TextField, QLogo } from '@netbasequid/core'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import { useDisplayTheme } from 'hooks/useUiState'
import { AutomationId } from 'utils/automationUtil'
import { useLiveReportInfo } from 'hooks/useLiveReportConfigModalState'
import { Theme } from 'features/ui/types'
import {
  StyledReportKeyValidationContainer,
  StyledReportKeyArea,
  StyledReportKeyValidationItem,
  StyledLoadingField,
  StyledLogo
} from './index.css'

const REPORT_KEY_VALIDATIONS = {
  LENGTH: /^.{8,15}$/,
  HAS_LETTER: /[a-zA-Z]/,
  HAS_NUMBER: /(?=.*\d)/,
  HAS_SPECIAL_CHAR: /[~!@#$%^&*()_\-+={}[\]\\|:;"'<>,.?/]/,
  NO_SPACE: /^\S+$/
}

const ReportKey = () => {
  const theme = useDisplayTheme()
  const liveReportInfo = useLiveReportInfo()
  const { t } = useTranslation()
  const { register, control, setValue } = useFormContext()
  const watchReportKey = useWatch({
    control,
    name: 'reportKey'
  })

  const validateReportKey = useCallback(
    (regexRule: RegExp, input: string, mode: Theme) => {
      const positiveColor = sys[mode].semantic.positive.default
      const placeholderColor = sys[mode].text.placeholder
      if (liveReportInfo.status === 'loading') {
        return placeholderColor
      }
      return regexRule.test(input) ? positiveColor : placeholderColor
    },
    [liveReportInfo]
  )

  useEffect(() => {
    if (liveReportInfo.status !== 'loading') {
      setValue('reportKey', atob(liveReportInfo.reportKey || ''))
    }
  }, [liveReportInfo, setValue])

  return (
    <StyledReportKeyArea data-testid={AutomationId.liveReportModal.reportKeyContainer}>
      {liveReportInfo.status === 'loading' ? (
        <StyledLoadingField mode={theme}>
          <StyledLogo>
            <QLogo fontSize={16} mode={theme} />
          </StyledLogo>
          {t('loadingThreeDot')}
        </StyledLoadingField>
      ) : (
        <TextField
          mode={theme}
          maxLength={15}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...register('reportKey', {
            required: true,
            minLength: 8,
            maxLength: 15,
            pattern: /^(?=[a-zA-Z\d~!@#$%^&*()_\-+={}[\]\\|:;"'<>,.?]{8,15}$)(?=.*[a-zA-Z])(?=.*\d)(?=.*[~!@#$%^&*()_\-+={}[\]\\|:;"'<>,.?])(?!.*\s).*$/
          })}
          placeholder={t('reportKeyPlaceholder')}
        />
      )}
      <StyledReportKeyValidationContainer
        data-testid={AutomationId.liveReportModal.reportKeyValidationContainer}>
        <StyledReportKeyValidationItem>
          <CheckSolid
            color={validateReportKey(REPORT_KEY_VALIDATIONS.LENGTH, watchReportKey, theme)}
          />
          {t('reportKeyValidation.length')}
        </StyledReportKeyValidationItem>
        <StyledReportKeyValidationItem>
          <CheckSolid
            color={validateReportKey(REPORT_KEY_VALIDATIONS.HAS_LETTER, watchReportKey, theme)}
          />
          {t('reportKeyValidation.letter')}
        </StyledReportKeyValidationItem>
        <StyledReportKeyValidationItem>
          <CheckSolid
            color={validateReportKey(REPORT_KEY_VALIDATIONS.HAS_NUMBER, watchReportKey, theme)}
          />
          {t('reportKeyValidation.number')}
        </StyledReportKeyValidationItem>
        <StyledReportKeyValidationItem>
          <CheckSolid
            color={validateReportKey(
              REPORT_KEY_VALIDATIONS.HAS_SPECIAL_CHAR,
              watchReportKey,
              theme
            )}
          />
          {t('reportKeyValidation.specialChar')}
        </StyledReportKeyValidationItem>
        <StyledReportKeyValidationItem>
          <CheckSolid
            color={validateReportKey(REPORT_KEY_VALIDATIONS.NO_SPACE, watchReportKey, theme)}
          />
          {t('reportKeyValidation.noSpace')}
        </StyledReportKeyValidationItem>
      </StyledReportKeyValidationContainer>
    </StyledReportKeyArea>
  )
}

export default ReportKey
